import { useState, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Warning } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'

import RadioField from '../FormLegacy/FormFieldsV2/RadioField'
import Filter from '../Filter/Filter'
import { selectBuildingOptions } from '../Organisation/CommunicationPortal/helpers'
import API from '../../api/api'

import SurveyQuestions from './SurveyQuestions'
import {
  getResponseTypeOptions,
  getSurveyTypeOptions,
  getSurveyCommunicationTypeOptions,
  renderSelectPlaceholder,
  getPropertyFilterOptions,
  multipleChoiceSurvey,
  singleChoiceSurvey,
  satisfactionSurvey,
  isCommunicationTypeSms,
  isCommunicationTypeEmail,
} from './helpers'

const Parameters = ({
  buildings,
  userRole,
  values,
  handleChange,
  setFieldValue,
  isEdit,
  isEditingDisabled,
  currentSurvey,
}) => {
  const { t } = useTranslation(['surveys'])
  const [filters, setFilters] = useState(null)
  const [yardiFilters, setYardiFilters] = useState([])

  useEffect(() => {
    const isYardiProperty = !!(
      values.buildings.length === 1 &&
      values.buildings[0].yardiManagementId &&
      values.buildings[0].yardiPropertyId
    )
    const isResmanProperty = !!(
      values.buildings.length === 1 &&
      values.buildings[0].resmanManagementId &&
      values.buildings[0].resmanPropertyId
    )

    const isRealPageProperty = !!(
      values.buildings.length === 1 &&
      values.buildings[0].realPageManagementId &&
      values.buildings[0].realPagePropertyId
    )

    if (isRealPageProperty || isYardiProperty || isResmanProperty) {
      API.getClientFieldsByBuildingId(values.buildings[0].id)
        .then((response) => {
          setFilters(response.data)
        })
        .catch((err) => console.error(err))
    }

    if (isYardiProperty) {
      API.getYardiPropertyIdsByBuildingId(values.buildings[0].id)
        .then((response) => {
          setYardiFilters(response.data)
        })
        .catch((err) => console.error(err))
    }
  }, [values.buildings])

  const handleBuildingsChange = (e) => {
    handleChange(e)
    setFieldValue('yardiPropertyId', null)
    setFieldValue('buildingNumber', null)
    setFieldValue('floor', null)
    setFilters(null)
    setYardiFilters([])
  }

  const yardiPropertyOptions = useMemo(
    () =>
      yardiFilters.length
        ? getPropertyFilterOptions(yardiFilters.map(({ yardiPropertyId }) => yardiPropertyId))
        : [],
    [yardiFilters],
  )

  const buildingOptions = useMemo(
    () => (filters ? getPropertyFilterOptions(filters.buildingNumber) : []),
    [filters],
  )

  const floorOptions = useMemo(
    () => (filters ? getPropertyFilterOptions(filters.floor) : []),
    [filters],
  )
  const preparedBuildings = currentSurvey
    ? values.buildings.map((selectedBuilding) =>
        buildings.find((building) => building.id === selectedBuilding.id),
      )
    : values.buildings

  // Note: `hasAllPreparedBuildingsAnnouncementPhone` returns true if all buildings have an announcement phone
  const hasAllPreparedBuildingsAnnouncementPhone = useMemo(
    () => preparedBuildings.every((building) => building.announcementPhone),
    [preparedBuildings],
  )

  return (
    <div className="survey-form__content">
      <div className="subtitle__wrap">
        <div className="survey-form__text">{t('surveys:form.buildingSelectLabel')}</div>
        <Filter
          name="buildings"
          placeholder={renderSelectPlaceholder(
            t,
            preparedBuildings,
            t('surveys:form.propertyLabel'),
          )}
          buttonClass="survey-form__input"
          options={selectBuildingOptions(buildings, userRole)}
          value={preparedBuildings}
          onChange={handleBuildingsChange}
          config={{
            multiple: true,
            hasSearch: true,
          }}
          disabled={isEdit}
        />
      </div>
      {!!yardiFilters.length || !!buildingOptions.length ? (
        <div className="survey-form__row">
          {!!yardiFilters.length && (
            <div className="subtitle__wrap">
              <div className="survey-form__text">{t('surveys:form.selectPhaseLabel')}</div>
              <Filter
                name="yardiPropertyId"
                placeholder={
                  values.yardiPropertyId
                    ? values.yardiPropertyId.text
                    : t('surveys:form.selectPhasePlaceholder')
                }
                options={yardiPropertyOptions}
                value={values.yardiPropertyId ? [values.yardiPropertyId] : []}
                onChange={handleChange}
                buttonClass="survey-form__input"
                config={{
                  multiple: false,
                  hasSearch: true,
                }}
                disabled={isEditingDisabled}
              />
            </div>
          )}
          {!!buildingOptions.length && (
            <div className="subtitle__wrap">
              <div className="survey-form__text">{t('surveys:form.selectBuildingLabel')}</div>
              <Filter
                name="buildingNumber"
                placeholder={
                  values.buildingNumber
                    ? values.buildingNumber.text
                    : t('surveys:form.selectBuildingPlaceholder')
                }
                buttonClass="survey-form__input"
                options={buildingOptions}
                value={values.buildingNumber ? [values.buildingNumber] : []}
                onChange={handleChange}
                config={{
                  multiple: false,
                  hasSearch: true,
                }}
                disabled={isEditingDisabled}
              />
            </div>
          )}
          {!!(buildingOptions.length && floorOptions.length) && (
            <div className="subtitle__wrap">
              <div className="survey-form__text">{t('surveys:form.selectFloorLabel')}</div>
              <Filter
                name="floor"
                placeholder={
                  values.floor ? values.floor.text : t('surveys:form.selectFloorPlaceholder')
                }
                buttonClass="survey-form__input"
                options={floorOptions}
                value={values.floor ? [values.floor] : []}
                onChange={handleChange}
                config={{
                  multiple: false,
                  hasSearch: true,
                }}
                disabled={isEditingDisabled}
              />
            </div>
          )}
        </div>
      ) : null}
      <div className="subtitle__wrap">
        <div className="survey-form__text">{t('surveys:form.communicationTypeLabel')}</div>
        <div className="radio-group">
          {getSurveyCommunicationTypeOptions(t).map(({ id, label, value }) => {
            const disableEmailMultipleChoice =
              isCommunicationTypeEmail(value) && values.type === multipleChoiceSurvey
            const disableSmsSingleMultipleChoice =
              isCommunicationTypeSms(value) &&
              (values.type === multipleChoiceSurvey || values.type === singleChoiceSurvey)

            return (
              <RadioField
                key={id}
                type="radio"
                name="communicationType"
                additionalClass="radio-group__item survey-form__text "
                label={label}
                onChange={(e) => {
                  handleChange(e)
                  setFieldValue('enablePushNotifications', false)
                }}
                checked={values.communicationType === value}
                value={value}
                disabled={
                  isEditingDisabled || disableEmailMultipleChoice || disableSmsSingleMultipleChoice
                }
              />
            )
          })}
        </div>
        {preparedBuildings.length > 0 && isCommunicationTypeSms(values.communicationType) ? (
          !hasAllPreparedBuildingsAnnouncementPhone ? (
            <div className="survey-form__warning survey-form__warning--error">
              <Warning size={16} />
              <div>{t('surveys:form.smsAnnouncementNumberError')}</div>
            </div>
          ) : (
            <div className="survey-form__warning survey-form__warning--warning">
              <Warning size={16} />
              <div>{t('surveys:form.smsCreditsWarning')}</div>
            </div>
          )
        ) : null}
      </div>
      <div className="subtitle__wrap">
        <div className="survey-form__text">{t('surveys:form.surveyTypeLabel')}</div>
        <div className="radio-group">
          {getSurveyTypeOptions(t).map(({ id, label, value }) => {
            const disableMultipleCoiseWhemEmailCommunication =
              value === multipleChoiceSurvey && isCommunicationTypeEmail(values.communicationType)
            const allowOnlySatysfactionSurveyWhenSmsCommunication =
              (value === multipleChoiceSurvey || value === singleChoiceSurvey) &&
              isCommunicationTypeSms(values.communicationType)

            return (
              <RadioField
                key={id}
                type="radio"
                name="type"
                additionalClass="radio-group__item survey-form__text "
                label={label}
                onChange={handleChange}
                checked={values.type === value}
                value={value}
                disabled={
                  isEditingDisabled ||
                  disableMultipleCoiseWhemEmailCommunication ||
                  allowOnlySatysfactionSurveyWhenSmsCommunication
                }
              />
            )
          })}
        </div>
      </div>
      {values.type === satisfactionSurvey ? (
        <div className="subtitle__wrap">
          <div className="survey-form__text">{t('surveys:form.responseTypeLabel')}</div>
          <div className="radio-group">
            {getResponseTypeOptions(t).map(({ id, label, value }) => (
              <RadioField
                key={id}
                type="radio"
                name="subtype"
                additionalClass="radio-group__item survey-form__text "
                label={label}
                onChange={handleChange}
                checked={values.subtype === value}
                value={value}
                disabled={isEditingDisabled}
              />
            ))}
          </div>
        </div>
      ) : null}
      <SurveyQuestions
        isEditingDisabled={isEditingDisabled}
        values={values}
        handleChange={handleChange}
      />
    </div>
  )
}

export default Parameters

Parameters.propTypes = {
  buildings: PropTypes.array.isRequired,
  values: PropTypes.object,
  handleChange: PropTypes.func,
  setFieldValue: PropTypes.func,
  errors: PropTypes.object,
  isEdit: PropTypes.bool,
  isEditingDisabled: PropTypes.bool,
  userRole: PropTypes.string,
  currentSurvey: PropTypes.object,
}
